/**
 * Journey Modal - Full-screen AI-Generated Journey Display
 *
 * Shows the LLM-generated return-to-India journey in a modal overlay.
 * Primary entry point is after Step 6 wizard completion.
 * Secondary access from Dashboard Summary tab.
 *
 * v2.0: Now supports rich premium components (HeroStats, TaxSavings, AIInsights, Scenarios)
 *
 * Email gate: Users must provide email before generating journey.
 * Rate limiting: 3 generations per week per email via Vercel KV.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '@/store/useAppStore';
import { useJourney } from '@/hooks/useJourney';
import { buildFactPack, loadJourney, type JourneyOutput, type JourneyPhase, type JourneyAction } from '@/lib/ai';
import { hashFactPackForCache } from '@/lib/utils/hash';
import { JourneyLoading } from './JourneyLoading';
import { JourneyError } from './JourneyError';
import { HeroStatsSection } from './HeroStatsSection';
import { TaxSavingsBreakdown } from './TaxSavingsBreakdown';
import { AIInsightsPanel } from './AIInsightsPanel';
import { ScenarioComparison } from './ScenarioComparison';
import { JourneyTimeline } from './JourneyTimeline';
import { MockDataBanner } from './MockDataBanner';
import { InputSummary } from './InputSummary';
import { EmailGateModal } from '@/components/shared/EmailGateModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  getSessionEmail,
  checkRateLimit,
  saveEmailToStorage,
  isValidEmail,
} from '@/lib/email/emailCapture';
import { EXTERNAL_LINKS } from '@/lib/config/externalLinks';

// Action category icons
const CATEGORY_ICONS: Record<string, string> = {
  tax: '💰',
  investment: '📈',
  compliance: '📋',
  account: '🏦',
  insurance: '🛡️',
  lifestyle: '🏠',
  timing: '⏰',
  documentation: '📄',
};

// Priority colors
const PRIORITY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  medium: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

// Phase status styles
const PHASE_STATUS_STYLES: Record<string, string> = {
  current: 'bg-primary-500 text-white',
  upcoming: 'bg-slate-200 text-slate-600',
  future: 'bg-slate-100 text-slate-400',
};

interface ActionCardProps {
  action: JourneyAction;
  isExpanded: boolean;
  onToggle: () => void;
}

function ActionCard({ action, isExpanded, onToggle }: ActionCardProps) {
  const priority = PRIORITY_STYLES[action.priority] ?? PRIORITY_STYLES.medium;
  const icon = CATEGORY_ICONS[action.category] || '📌';
  
  // v2.0: Check for estimated savings
  const hasSavings = action.estimatedSavingsINR && action.estimatedSavingsINR > 0;

  return (
    <motion.div
      layout
      className={cn(
        'rounded-xl border p-4 cursor-pointer transition-all',
        priority?.bg,
        priority?.border,
        isExpanded && 'ring-2 ring-primary-300'
      )}
      onClick={onToggle}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className={cn('font-medium', priority?.text)}>{action.title}</h4>
            {action.isTimeSensitive && (
              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                Time-sensitive
              </span>
            )}
            {/* v2.0: Show savings badge */}
            {hasSavings && (
              <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                💰 {action.estimatedSavingsFormatted}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 line-clamp-2">{action.description}</p>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 pt-3 border-t border-slate-200"
              >
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">Why this matters: </span>
                    <span className="text-slate-600">{action.rationale}</span>
                  </div>
                  
                  {/* v2.0: Impact description */}
                  {action.impactDescription && (
                    <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                      <span className="font-medium text-emerald-700">Impact: </span>
                      <span className="text-emerald-600">{action.impactDescription}</span>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-3">
                    <span className="text-slate-500">
                      <strong>Timing:</strong> {action.suggestedTiming}
                    </span>
                    {action.deadline && (
                      <span className="text-red-600">
                        <strong>Deadline:</strong> {action.deadline}
                      </span>
                    )}
                  </div>
                  {action.requiresCAReview && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 rounded-lg">
                      <span className="text-amber-500">⚠️</span>
                      <span className="text-amber-700 text-xs">
                        Requires CA Review: {action.caReviewReason}
                      </span>
                    </div>
                  )}
                  {action.sourceReference && (
                    <div className="text-xs text-slate-400 mt-2">
                      Source: {action.sourceReference}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <span className={cn('px-2 py-1 text-xs rounded-full font-medium', priority?.bg, priority?.text)}>
          {action.priority}
        </span>
      </div>
    </motion.div>
  );
}

interface PhaseCardProps {
  phase: JourneyPhase;
  isOpen: boolean;
  onToggle: () => void;
}

function PhaseCard({ phase, isOpen, onToggle }: PhaseCardProps) {
  const [expandedAction, setExpandedAction] = useState<string | null>(null);
  
  // v2.0: Check for phase-level stats
  const hasPhaseSavings = phase.estimatedSavingsINR && phase.estimatedSavingsINR > 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader
        className={cn(
          'cursor-pointer transition-colors',
          isOpen ? 'bg-primary-50' : 'hover:bg-slate-50'
        )}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold',
                PHASE_STATUS_STYLES[phase.status]
              )}
            >
              {phase.sequence}
            </div>
            <div>
              <CardTitle className="text-lg">{phase.name}</CardTitle>
              <p className="text-sm text-slate-500">
                {phase.dateRange.start} – {phase.dateRange.end}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* v2.0: Show phase savings */}
            {hasPhaseSavings && (
              <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                💰 Potential savings
              </span>
            )}
            <span className="text-sm text-slate-500">{phase.actions.length} actions</span>
            {phase.criticalActions && phase.criticalActions > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                {phase.criticalActions} critical
              </span>
            )}
            <motion.span
              animate={{ rotate: isOpen ? 180 : 0 }}
              className="text-slate-400"
            >
              ▼
            </motion.span>
          </div>
        </div>

        {/* Action preview when collapsed - shows top 2-3 actions with details */}
        {!isOpen && phase.actions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="space-y-2">
              {phase.actions
                .slice()
                .sort((a, b) => {
                  const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2 };
                  return (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
                })
                .slice(0, 3)
                .map((action) => {
                  const defaultStyle = { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
                  const priorityStyle = PRIORITY_STYLES[action.priority] || defaultStyle;
                  return (
                    <div
                      key={action.id}
                      className={cn(
                        'rounded-lg p-2.5 border',
                        priorityStyle.bg,
                        priorityStyle.border
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            {action.priority === 'critical' && <span className="text-xs">🔴</span>}
                            {action.priority === 'high' && <span className="text-xs">🟡</span>}
                            <span className={cn('text-sm font-medium', priorityStyle.text)}>
                              {action.title}
                            </span>
                          </div>
                          {action.deadline && (
                            <p className="text-xs text-slate-500">
                              <span className="font-medium">Deadline:</span> {action.deadline}
                            </p>
                          )}
                          {action.impactDescription && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              <span className="font-medium">Impact:</span> {action.impactDescription}
                            </p>
                          )}
                        </div>
                        <span className={cn('px-1.5 py-0.5 text-[10px] rounded font-medium', priorityStyle.bg, priorityStyle.text)}>
                          {action.priority}
                        </span>
                      </div>
                    </div>
                  );
                })}
              {phase.actions.length > 3 && (
                <p className="text-xs text-slate-500 text-center">
                  +{phase.actions.length - 3} more actions • Click to expand
                </p>
              )}
            </div>
          </div>
        )}
      </CardHeader>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <CardContent className="pt-4">
              <p className="text-slate-600 mb-4">{phase.description}</p>
              <div className="space-y-3">
                {phase.actions
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((action) => (
                    <ActionCard
                      key={action.id}
                      action={action}
                      isExpanded={expandedAction === action.id}
                      onToggle={() =>
                        setExpandedAction(expandedAction === action.id ? null : action.id)
                      }
                    />
                  ))}
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// Journey display content (reusable)
interface JourneyDisplayProps {
  journey: JourneyOutput;
  openPhases: Set<string>;
  togglePhase: (phaseId: string) => void;
  onRegenerate: () => void;
  remainingGenerations: number;
  isGenerating: boolean;
  factPack: ReturnType<typeof buildFactPack>;
}

function JourneyDisplay({
  journey,
  openPhases,
  togglePhase,
  onRegenerate,
  remainingGenerations,
  isGenerating,
  factPack,
}: JourneyDisplayProps) {
  // Guard against undefined journey data
  if (!journey?.summary || !journey?.timeline || !journey?.phases) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-600 mb-4">Unable to load journey data. Please try regenerating.</p>
        <button
          onClick={onRegenerate}
          disabled={isGenerating || remainingGenerations <= 0}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {isGenerating ? 'Generating...' : 'Regenerate Journey'}
        </button>
      </div>
    );
  }

  // Check if journey is v2.0 (has heroStats)
  const isV2 = journey.schemaVersion === '2.0' && !!journey.heroStats;

  return (
    <div className="space-y-8">
      {/* Mock Data Warning Banner */}
      <MockDataBanner />

      {/* v2.0: Hero Stats Section - The "Wow" Moment */}
      {isV2 && journey.heroStats ? (
        <HeroStatsSection stats={journey.heroStats} />
      ) : (
        /* v1.0 Fallback: Basic Journey Header */
        <Card className="bg-gradient-to-r from-primary-500 to-primary-600 text-white border-0">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold mb-1">{journey.summary.headline}</h2>
                <p className="text-white/80">{journey.summary.subheadline}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center px-4 py-2 bg-white/20 rounded-xl">
                  <div className="text-sm text-white/70">Timeline</div>
                  <div className="text-xl font-bold">{journey.timeline.totalMonths} months</div>
                </div>
                <div className="text-center px-4 py-2 bg-white/20 rounded-xl">
                  <div className="text-sm text-white/70">Confidence</div>
                  <div className="text-xl font-bold capitalize">
                    {journey.summary.confidenceLevel}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fallback Warning */}
      {journey._isFallback && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <span className="text-amber-500 text-xl">⚠️</span>
          <div>
            <h4 className="font-medium text-amber-800">Template-based Journey</h4>
            <p className="text-sm text-amber-700">
              This journey was generated from a template due to a service issue. Try regenerating
              for a more personalized plan.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={onRegenerate}
              disabled={remainingGenerations === 0}
            >
              Regenerate Journey
            </Button>
          </div>
        </div>
      )}

      {/* v2.0: Tax Savings Breakdown */}
      {isV2 && journey.taxSavingsHighlight && (
        <TaxSavingsBreakdown savings={journey.taxSavingsHighlight} />
      )}

      {/* Input Summary - Show users what drove these recommendations */}
      <InputSummary factPack={factPack} />

      {/* v2.0: AI Insights Panel */}
      {isV2 && journey.aiInsights && journey.aiInsights.length > 0 && (
        <AIInsightsPanel insights={journey.aiInsights} />
      )}

      {/* v2.0: Scenario Comparison */}
      {isV2 && journey.scenarioComparison && (
        <ScenarioComparison comparison={journey.scenarioComparison} />
      )}

      {/* Strategic CA CTA - After scenarios for users making timing decisions */}
      {isV2 && journey.scenarioComparison && (
        <div className="p-5 bg-gradient-to-r from-primary-50 to-indigo-50 rounded-xl border border-primary-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🎯</span>
              </div>
              <div>
                <h4 className="font-semibold text-primary-800 mb-1">
                  Uncertain About the Best Scenario?
                </h4>
                <p className="text-sm text-slate-600">
                  A tax expert can analyze your specific situation and recommend the optimal return timing based on your goals.
                </p>
              </div>
            </div>
            <a
              href={EXTERNAL_LINKS.calendly.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap shadow-md"
            >
              <span>📞</span>
              <span>Talk to NRI Tax Expert</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      )}

      {/* Visual Timeline - Gantt Chart */}
      <JourneyTimeline
        targetReturnDate={journey.timeline.targetReturnDate}
        rnorEndDate={journey.timeline.rnorEndDate}
        milestones={journey.timeline.milestones}
        rnorYears={isV2 && journey.heroStats ? journey.heroStats.rnorBenefit.years : 2}
      />

      {/* Phases */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <span>🗺️</span> Your Journey Phases
        </h3>
        {journey.phases.map((phase) => (
          <PhaseCard
            key={phase.id}
            phase={phase}
            isOpen={openPhases.has(phase.id)}
            onToggle={() => togglePhase(phase.id)}
          />
        ))}
      </div>

      {/* Key Decision Points */}
      {journey.keyDecisionPoints.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>🎯</span> Key Decision Points
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {journey.keyDecisionPoints.map((point) => (
                <div
                  key={point.id}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-slate-800">{point.title}</h4>
                    <span className="text-sm text-slate-500">
                      Decide by: {point.suggestedDecisionDate}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{point.description}</p>
                  {point.options && point.options.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {point.options.map((option, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm"
                        >
                          {option}
                        </span>
                      ))}
                    </div>
                  )}
                  {point.requiresProfessionalReview && (
                    <div className="mt-3 text-xs text-amber-600 flex items-center gap-1">
                      <span>⚠️</span>
                      Consult with a {point.reviewType.replace('_', ' ')} before deciding
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disclaimers */}
      <Card className="bg-slate-50 border-slate-200">
        <CardHeader>
          <CardTitle className="text-base text-slate-600">Important Disclaimers</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-500 space-y-3">
          <p>{journey.disclaimers.standard}</p>

          {journey.disclaimers.itemsRequiringCAReview.length > 0 && (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h5 className="font-medium text-amber-800 mb-2">
                {journey.disclaimers.itemsRequiringCAReview.length} item{journey.disclaimers.itemsRequiringCAReview.length > 1 ? 's' : ''} need professional review:
              </h5>
              <ul className="list-disc list-inside text-amber-700 space-y-1 mb-4">
                {journey.disclaimers.itemsRequiringCAReview.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              {/* Contextual CA Expert CTA */}
              <a
                href={EXTERNAL_LINKS.calendly.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors"
              >
                <span>📞</span>
                <span>Get These Reviewed by CA Expert</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          )}

          {journey.disclaimers.dataLimitations.length > 0 && (
            <div>
              <h5 className="font-medium text-slate-600 mb-1">Data Limitations:</h5>
              <ul className="list-disc list-inside text-slate-500">
                {journey.disclaimers.dataLimitations.map((limitation, idx) => (
                  <li key={idx}>{limitation}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regenerate Button */}
      <div className="flex justify-center pt-4">
        <Button
          variant="outline"
          onClick={onRegenerate}
          disabled={remainingGenerations === 0 || isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Regenerate Journey'}
        </Button>
        <p className="text-slate-400 text-xs ml-4 self-center">
          {remainingGenerations} remaining
        </p>
      </div>
    </div>
  );
}

export interface JourneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  autoGenerate?: boolean;
}

export function JourneyModal({ isOpen, onClose, autoGenerate = true }: JourneyModalProps) {
  const appState = useAppStore((state) => state);
  const [journey, setJourney] = useState<JourneyOutput | null>(null);
  const [openPhases, setOpenPhases] = useState<Set<string>>(new Set());
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);

  // Email gate state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [serverRemainingGenerations, setServerRemainingGenerations] = useState<number | null>(null);
  const [dailyRemaining, setDailyRemaining] = useState<number | null>(null);
  const [weeklyRemaining, setWeeklyRemaining] = useState<number | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);

  // Build fact pack from current state - memoized to prevent infinite re-renders
  const factPack = useMemo(() => buildFactPack(appState), [appState]);

  // Use the journey hook
  const {
    generate,
    isGenerating,
    error,
    progress,
    remainingGenerations: hookRemainingGenerations,
    preComputedData, // For rate limit preview
  } = useJourney({
    factPack,
    onSuccess: (result, remaining) => {
      setJourney(result);
      // Update remaining from server response
      if (remaining !== undefined) {
        setServerRemainingGenerations(remaining);
      }
      // Open first phase by default
      if (result.phases.length > 0 && result.phases[0]) {
        setOpenPhases(new Set([result.phases[0].id]));
      }
    },
  });

  // Use server-side remaining if available, otherwise hook default
  const remainingGenerations = serverRemainingGenerations ?? hookRemainingGenerations;

  // Check rate limit on mount if email is captured
  useEffect(() => {
    if (!isOpen) return;

    const checkLimit = async () => {
      const email = getSessionEmail();
      setSessionEmail(email);
      if (email) {
        const limit = await checkRateLimit(email);
        setServerRemainingGenerations(limit.remaining);
        setDailyRemaining(limit.dailyRemaining);
        setWeeklyRemaining(limit.weeklyRemaining);
      }
    };
    checkLimit();
  }, [isOpen]);

  // Compute hash once for stable dependency
  const factPackHash = useMemo(
    () => hashFactPackForCache(factPack as Record<string, unknown>),
    [factPack]
  );

  // Load stored journey on mount - BUT check if user data has changed
  useEffect(() => {
    if (!isOpen) return;

    const stored = loadJourney();

    if (stored) {
      // Compare with stored hash
      const dataHasChanged = stored.factPackHash !== factPackHash;

      if (dataHasChanged) {
        // Data changed - clear cached journey and reset auto-generate flag
        setJourney(null);
        setHasAutoGenerated(false);
        return;
      }

      // Data hasn't changed - safe to use cached journey
      setJourney(stored.journey);
      const currentPhase = stored.journey.phases.find((p) => p.status === 'current');
      if (currentPhase) {
        setOpenPhases(new Set([currentPhase.id]));
      } else if (stored.journey.phases.length > 0 && stored.journey.phases[0]) {
        setOpenPhases(new Set([stored.journey.phases[0].id]));
      }
    }
  }, [isOpen, factPackHash]);

  // Auto-generate on first open OR when data changed - show email modal if no email captured
  useEffect(() => {
    if (!isOpen || !autoGenerate) return;
    if (hasAutoGenerated) return; // Already triggered auto-generate this session
    if (journey) return; // Already have a journey
    if (remainingGenerations <= 0) return;

    setHasAutoGenerated(true);

    // Check if email is already captured
    const email = getSessionEmail();
    if (email) {
      // Email captured, generate directly
      generate(email);
    } else {
      // Show email modal
      setShowEmailModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoGenerate, hasAutoGenerated, journey]);

  // Handle generate button click - show email modal
  const handleGenerateClick = useCallback(() => {
    setShowEmailModal(true);
  }, []);

  // Navigate to wizard to complete profile
  const handleCompleteProfile = useCallback((step?: number) => {
    onClose(); // Close modal first
    window.dispatchEvent(
      new CustomEvent('navigate-to-wizard', { detail: { step: step || 1 } })
    );
  }, [onClose]);

  // Handle email submission from modal
  const handleEmailSubmit = useCallback(async (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();

    // Validate email format
    if (!isValidEmail(trimmedEmail)) {
      return; // Modal will handle display
    }

    setIsSubmittingEmail(true);

    try {
      // Check rate limit for early blocking
      const rateLimit = await checkRateLimit(trimmedEmail);
      setDailyRemaining(rateLimit.dailyRemaining);
      setWeeklyRemaining(rateLimit.weeklyRemaining);
      if (!rateLimit.canGenerate) {
        setServerRemainingGenerations(0);
        setIsSubmittingEmail(false);
        return;
      }

      // Update session email
      setSessionEmail(trimmedEmail);

      // Close modal
      setShowEmailModal(false);
      setIsSubmittingEmail(false);

      // Generate journey - server handles rate limiting + LLM call
      await generate(trimmedEmail);

      // Save email to local storage for future sessions
      saveEmailToStorage(trimmedEmail);
    } catch (err) {
      console.error('Email submission error:', err);
      setIsSubmittingEmail(false);
    }
  }, [generate]);

  const togglePhase = useCallback((phaseId: string) => {
    setOpenPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }
      return next;
    });
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="journey-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isGenerating) {
              onClose();
            }
          }}
        >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed inset-4 md:inset-8 lg:inset-12 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                <span className="text-xl">🗺️</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Your Return Journey</h2>
                <p className="text-sm text-slate-500">AI-powered personalized roadmap</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isGenerating}
              className="text-slate-500 hover:text-slate-700"
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>

          {/* Modal Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Show loading state */}
            {isGenerating && <JourneyLoading stage={progress.stage} />}

            {/* Show error state */}
            {error && !journey && !isGenerating && (
              <JourneyError
                error={error}
                hasFallback={false}
                preComputedData={preComputedData}
                fireSnapshot={factPack.fireSnapshot}
                onRetry={() => {
                  if (sessionEmail) {
                    generate(sessionEmail);
                  } else {
                    setShowEmailModal(true);
                  }
                }}
                onViewFallback={() => {
                  if (sessionEmail) {
                    generate(sessionEmail);
                  } else {
                    setShowEmailModal(true);
                  }
                }}
                onGoToDashboard={onClose}
              />
            )}

            {/* Show empty state with generate button */}
            {!journey && !isGenerating && !error && (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-4xl">🗺️</span>
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Your Personalized Journey</h2>
                <p className="text-slate-600 text-center max-w-md mb-6">
                  Generate an AI-powered roadmap tailored to your unique situation, with actionable steps
                  for tax optimization, compliance, and timing your return.
                </p>
                <Button
                  onClick={handleGenerateClick}
                  disabled={remainingGenerations === 0}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-8 py-3"
                >
                  Generate My Journey
                </Button>
                {remainingGenerations === 0 && (
                  <p className="text-amber-600 text-sm mt-3">
                    {dailyRemaining === 0 && weeklyRemaining !== null && weeklyRemaining > 0
                      ? "Daily limit reached (2/day). Try again tomorrow."
                      : "Weekly limit reached (3/week). Try again next week."}
                  </p>
                )}
                {remainingGenerations > 0 && (
                  <p className="text-slate-400 text-xs mt-3">
                    {dailyRemaining !== null && weeklyRemaining !== null
                      ? `${dailyRemaining}/2 today • ${weeklyRemaining}/3 this week`
                      : `${remainingGenerations} generation${remainingGenerations !== 1 ? 's' : ''} remaining`}
                  </p>
                )}
              </div>
            )}

            {/* Show journey */}
            {journey && !isGenerating && (
              <JourneyDisplay
                journey={journey}
                openPhases={openPhases}
                togglePhase={togglePhase}
                onRegenerate={handleGenerateClick}
                remainingGenerations={remainingGenerations}
                isGenerating={isGenerating}
                factPack={factPack}
              />
            )}
          </div>

          {/* Email Gate Modal */}
          <EmailGateModal
            isOpen={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            onSubmit={handleEmailSubmit}
            isLoading={isSubmittingEmail}
            remainingGenerations={remainingGenerations}
            dailyRemaining={dailyRemaining ?? undefined}
            weeklyRemaining={weeklyRemaining ?? undefined}
            onCompleteProfile={handleCompleteProfile}
          />

          {/* Modal Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white">
            <p className="text-xs text-slate-400">
              AI-generated roadmap. Questions?{' '}
              <a
                href={EXTERNAL_LINKS.calendly.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
              >
                Talk to an NRI Tax Expert
              </a>
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isGenerating}
              >
                {journey ? 'Go to Dashboard' : 'Skip for Now'}
              </Button>
              {journey && (
                <Button
                  onClick={onClose}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700"
                >
                  Continue to Dashboard
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
}

export default JourneyModal;
